// CustomOutputNodeComponent.tsx
import React, { useEffect, useMemo } from 'react';
import { Handle, Position } from 'reactflow';
import Node, { contentStyle as style } from './Node';
import Chip from '@mui/material/Chip';
import { useStore } from './store';
import { Input, CustomNodeData} from './types';
import Tooltip from '@mui/material/Tooltip';
import { LineChart } from '@mui/x-charts/LineChart';
import Typography from '@mui/material/Typography';
import { ChartContainer, mangoFusionPalette } from '@mui/x-charts';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { LinePlot, MarkPlot } from '@mui/x-charts/LineChart';
import { ChartsLegend } from '@mui/x-charts/ChartsLegend';



interface Output {
  OutputFlowID: { low: number; high?: number };
  OutputFlowName: string;
  FlowType: string;
  AmountValue: number;
  UnitName: string;
  LocationName: string;
  Hscode: string;
  HscodeName: string;
}

interface CustomOutputNodeData {
  LocationName: string;
  ProcessName: string;
  outputs: Output[];
  title: string;
  flowID: number; // Assuming this is available and correctly typed
  processID: number; // Assuming this is available and correctly typed

}

interface ProductTotals {
  [productName: string]: number;
}

interface TotalsByDate {
  [dateKey: string]: ProductTotals;
}

const totalsByDate: TotalsByDate = {};


const CustomOutputNodeComponent: React.FC<{ data: CustomOutputNodeData}> = ({ data }) => {
 
  const nodeID = `parent-${data.flowID}-${data.processID}`;

  const { selectedYear, selectedMonth } = useStore(state => ({
    selectedYear: state.selectedYear,
    selectedMonth: state.selectedMonth,
  }));
  
  const nodeTotalForSelectedPeriod = useStore(state => {
    const totals = state.nodeTotals[nodeID] || [];
    const totalEntry = totals.find(t => t.REFYEAR === selectedYear && t.REFMONTH === selectedMonth);
    return totalEntry ? totalEntry.total : 0;
  });

  const { priceHistory } = useStore(state => ({
    priceHistory: state.priceHistory,
}));

const { dollarValues } = useStore(state => ({
  dollarValues: state.dollarValues,
}));


const dollarValuesForNodeId = useMemo(() => {
  // Filter keys that start with nodeID
  const filteredKeys = Object.keys(dollarValues).filter(key => key.startsWith(nodeID));
  // Map over filtered keys to get objects that include both the key and its associated values
  const keyValuePairs = filteredKeys.map(key => ({
    key,
    values: dollarValues[key],
  }));

  // Flatten the array of values while retaining the key in each value object
  const flattenedValuesWithKeys = keyValuePairs.flatMap(pair =>
    pair.values.map(value => ({
      key: pair.key,
      ...value,
    }))
  );

  return flattenedValuesWithKeys;
}, [dollarValues, nodeID]);

const DollarValuesOutput = (
  <div>
    <h3>Dollar Values (filtered by nodeID)</h3>
    <ul>
      {dollarValuesForNodeId.map((item, index) => (
        <li key={index}>
          Key: {item.ProductName}, Year: {item.REFYEAR}, Month: {item.REFMONTH}, Total: ${item.calculatedTotal.toFixed(2)}
        </li>
      ))}
    </ul>
  </div>
);

const stackedAreaChartData = useMemo(() => {
  const totalsByDate: TotalsByDate = {};

  // Aggregate dollarValues by date and ProductName
  dollarValuesForNodeId.forEach(({REFYEAR, REFMONTH, ProductName, calculatedTotal}) => {
    const dateKey = `${REFYEAR}-${String(REFMONTH).padStart(2, '0')}`;
    if (!totalsByDate[dateKey]) {
      totalsByDate[dateKey] = {};
    }
    const productKey = ProductName || "Unknown"; // Fallback for missing ProductName
    totalsByDate[dateKey][productKey] = (totalsByDate[dateKey][productKey] || 0) + calculatedTotal;
  });

  // Convert aggregated totals to series data for the chart
  const productNames = new Set(dollarValuesForNodeId.map(item => item.ProductName || "Unknown"));
  const series = Array.from(productNames).map(productName => {
    const data = Object.keys(totalsByDate).sort().map(dateKey => {
      const value = totalsByDate[dateKey][productName] || 0;
      return { date: dateKey, value };
    });
    return { id: productName, label: productName, data, area: true, stack: 'total' };
  }).filter(series => {
    // Check if the series has any non-zero values
    return series.data.some(point => point.value !== 0);
  });

  const xAxisData = Object.keys(totalsByDate).sort();

  return { series, xAxisData };
}, [dollarValuesForNodeId]);





// Prepare the chart data
const chartData = useMemo(() => {
  return data.outputs.map(output => {
    const historyItems = (priceHistory[output.Hscode] || []).sort((a, b) => {
      const aDate = new Date(a.REFYEAR, a.REFMONTH - 1, 1);
      const bDate = new Date(b.REFYEAR, b.REFMONTH - 1, 1);
      return aDate.getTime() - bDate.getTime();
    });
      // Convert REFYEAR and REFMONTH to timestamps for the xAxis
      const xAxisData = historyItems.map(item => {
        const date = new Date(item.REFYEAR, item.REFMONTH - 1, 1);
        const formattedDate = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}`;
        return formattedDate;
      });
      const seriesData = historyItems.map(item => item.AVERAGEUNITVALUE);

      return {
          id: output.Hscode,
          xAxisData,
          seriesData,
      };
  });
}, [data.outputs, priceHistory]);


const { nodeTotals } = useStore(state => ({
  nodeTotals: state.nodeTotals,
}));

// Extract the node totals over time for plotting
const nodeTotalsData = useMemo(() => {
  const totals = nodeTotals[nodeID] || [];
  // Sort the totals by date
  totals.sort((a, b) => {
    const dateA = new Date(a.REFYEAR, a.REFMONTH - 1);
    const dateB = new Date(b.REFYEAR, b.REFMONTH - 1);
    return dateA.getTime() - dateB.getTime();
  });
  // Prepare the data for charting
   const xAxisData = totals.map(t => {
    const date = new Date(t.REFYEAR, t.REFMONTH - 1, 1);
    const formattedDate = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}`;
    return formattedDate;
  });
  const seriesData = totals.map(t => t.total);
  return { 
    id: nodeID,
    xAxisData, 
    seriesData };
}, [nodeTotals, nodeID]);



const combinedChartData = useMemo(() => {
  // Combining chartData series and nodeTotalsData into a single series array
  const combinedSeries = [
    ...chartData.map(({ id, seriesData }) => ({
      id,
      label: 'Export Price',
      data: seriesData,
    })),
    {
      id: nodeTotalsData.id,
      label: 'Should Cost Estimate',
      data: nodeTotalsData.seriesData,
    },
  ];

  return {
    xAxisData: nodeTotalsData.xAxisData, // Assuming a common xAxis for simplicity
    series: combinedSeries,
  };
}, [chartData, nodeTotalsData]);

const xAxisData = combinedChartData.xAxisData;

// A function to align series data with the X axis data
function alignSeriesWithDataPoints(xAxisData: string[], seriesData: any[]) {
  const alignedData = xAxisData.map(xDate => {
    // Find the series data point that matches the current X axis date
    const dataPoint = seriesData.find(point => point.date === xDate);
    // If found, return its value; otherwise, return null
    return dataPoint ? dataPoint.value : null;
  });

  return alignedData;
}


const adjustedSeries = combinedChartData.series.map(series => {
  // Align this series data with the X axis data
  const alignedSeriesData = alignSeriesWithDataPoints(xAxisData, series.data.map((value, index) => ({
    date: xAxisData[index], // Assuming series.data is aligned with xAxisData
    value,
  })));

  return {
    ...series,
    data: alignedSeriesData,
    // Optionally use valueFormatter to handle display of null values
    valueFormatter: (value: number | null) => value == null ? 'No Data' : value.toString(),
  };
});

  
  // Access nodeTotal from the store using nodeID
  const nodeTotal = useStore(state => state.nodeTotals[nodeID] || 0);

   // Effect hook to log the nodeTotal whenever it changes
   useEffect(() => {
    console.log(`NodeTotal for nodeID ${nodeID}: ${nodeTotal}`);
  }, [nodeTotal, nodeID]);
  
  const NodeContent = (
    <>
    <div>
    <Handle type="target" position={Position.Left}   style={{ ...style.handle, ...style.left }} />
      <div >
      {data.outputs.map((output, index) => (
       <div key={`output-${index}`} style={{
          ...style.io,
          ...style.flexRow,
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'relative',
      }}>
       <div >{data.ProcessName}</div>
       </div>
      ))}
      <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
      

      {data.outputs.map((output, index) => (
        
            <div key={`output-${index}`} style={{
              ...style.io,
              ...style.flexRow,
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative',
          }}>
               <Tooltip title={output.HscodeName} placement="left">
      <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ flexGrow: 1  }}>
                                <div style={{ fontWeight: 'bold' }}>{output.OutputFlowName}</div>
                                <div style={{ fontSize: 'small' }}> <Chip label= {data.LocationName}  size="small"/></div>
                            </div>
                            <div style={{ display: 'flex' }}>
                                <div style={style.unitcard}>{output.UnitName}</div>
                                <div style={style.amountcard}>{output.AmountValue}</div>
                                <div style={{ ...style.subtotalamountcard, flexDirection: 'column', justifyContent: 'center', alignItems: 'center', display: 'flex' }}>
                                    <label style={{ fontSize: '0.7em', marginBottom: '5px' }}>Avg.</label>
                                    <div style={{
                                        border: 'none', // Remove any default browser input border
                                        background: 'transparent', // Remove default background
                                        textAlign: 'center', // Center text
                                        width: '100%', // Use full width of the card
                                        outline: 'none', // Remove focus outline
                                        fontSize: '0.8em',
                                    }}>  ${nodeTotalForSelectedPeriod.toFixed(2)}
                                    </div>
                                </div>
                                <div style={{ ...style.subtotalamountcard, flexDirection: 'column', justifyContent: 'center', alignItems: 'center', display: 'flex' }}>
                                    <label style={{ fontSize: '0.7em', marginBottom: '5px' }}>Total $ items</label>
                                    <div style={{
                                        border: 'none', // Remove any default browser input border
                                        background: 'transparent', // Remove default background
                                        textAlign: 'center', // Center text
                                        width: '100%', // Use full width of the card
                                        outline: 'none', // Remove focus outline
                                        fontSize: '0.8em',
                                    }}>$
                                           ${nodeTotalForSelectedPeriod.toFixed(2)}
                                    </div>
                                </div>

                            </div>
                        </div>
                        </Tooltip>
            </div>
        
            ))}
          
      </div>
      </div>
     
    </div>
    <div>
    <LineChart
  xAxis={[{ scaleType: 'point', data: combinedChartData.xAxisData }]}
  series={adjustedSeries}
  width={500}
  height={300}
  margin={{ bottom: 50, }}
  slotProps={{
    legend: {
      direction: 'row',
      itemMarkWidth: 5,
      itemMarkHeight: 5,
      position: { vertical: 'bottom', horizontal: 'middle' },
      padding: 0,
      labelStyle: {
        fontSize: 8,
        fill: 'black',
      },
    },
  }}
/>
<LineChart
  xAxis={[{ scaleType: 'point', data: stackedAreaChartData.xAxisData }]}
  series={stackedAreaChartData.series.map(s => ({
    ...s,
    showMark: false,
    stackOrder: 'descending',
      data: s.data.map(d => d.value)
  }))}
  margin={{ top: 50, bottom: 120, }}
  width={500}
  height={350}
  colors={mangoFusionPalette}
  slotProps={{
    legend: {
      direction: 'row',
      itemMarkWidth: 5,
      itemMarkHeight: 5,
      position: { vertical: 'bottom', horizontal: 'middle' },
      padding: 10,
      labelStyle: {
        fontSize: 8,
        fill: 'black',
      },
    },
  }}
/>

 </div>
        </>
    );
  return <Node label={data.title} selected={false} color="#2D8056" content={NodeContent} />;
};

  export default CustomOutputNodeComponent;